import React, { useState, useEffect } from 'react';
import { Table, Select, Input, Tag, DatePicker, Space, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
const { RangePicker } = DatePicker;

const statusColors = {
  assigned: 'processing', delivered: 'success', failed: 'error', transferred: 'default',
  postponed: 'warning', tomorrow: 'warning', phone_not_answered: 'warning',
  cannot_contact: 'warning', wrong_phone: 'error', unclear_address: 'error',
  gate_locked: 'warning', entrance_chip: 'warning', other: 'default',
};
const statusLabels = {
  assigned: 'Хүргэлтэнд', delivered: 'Хүргэгдсэн', failed: 'Цуцалсан', transferred: 'Шилжүүлсэн',
  postponed: 'Хойшлогдсон', tomorrow: 'Маргааш авах', phone_not_answered: 'Утас авахгүй',
  cannot_contact: 'Холбогдохгүй', wrong_phone: 'Дугаар буруу', unclear_address: 'Хаяг тодорхойгүй',
  gate_locked: 'Хотхоны хаалт', entrance_chip: 'Орц байрны чип', other: 'Бусад',
};

// Dispatched: Баасан бол тухайн өдрийн 18:30, бусад бол маргаашийн 09:30
const getDispatchedDate = (dateStr) => {
  if (!dateStr) return null;
  const d = dayjs(dateStr).utcOffset(8);
  if (d.day() === 5) return d.hour(18).minute(30).second(0);
  return d.add(1, 'day').hour(9).minute(30).second(0);
};

const Deliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const today = dayjs().format('YYYY-MM-DD');
  const [filters, setFilters] = useState({ startDate: today, endDate: today });
  const [search, setSearch] = useState('');

  const fetchDeliveries = async (p = page, f = filters) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 50, ...f };
      if (search) params.search = search;
      const res = await api.get('/deliveries', { params });
      setDeliveries(res.data.deliveries);
      setTotal(res.data.total);
    } catch (error) {
      message.error('Хүргэлт татахад алдаа гарлаа');
    }
    setLoading(false);
  };

  useEffect(() => { fetchDeliveries(1, filters); }, []);

  const handleSearch = () => {
    setPage(1);
    fetchDeliveries(1, filters);
  };

  const columns = [
    { title: 'Дугаар', key: 'orderNumber', width: 120,
      render: (_, r) => r.order?.orderNumber || '-' },
    { title: 'Хүлээн авагч', key: 'receiverName',
      render: (_, r) => r.order?.receiverName || '-' },
    { title: 'Утас', key: 'phone', width: 110,
      render: (_, r) => r.order?.phone1 || '-' },
    { title: 'Дүүрэг', key: 'district', width: 120,
      render: (_, r) => r.order?.district?.name || '-' },
    { title: 'Хороо', key: 'khoroo', width: 80,
      render: (_, r) => r.order?.khoroo ? `${r.order.khoroo}-р` : '-' },
    { title: 'Жолооч', key: 'driver', width: 130,
      render: (_, r) => r.driver?.name || '-' },
    { title: 'Төлөв', dataIndex: 'status', key: 'status', width: 140,
      render: s => <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag> },

    { title: 'Огноо', key: 'dispatched', width: 140,
      render: (_, r) => { const d = getDispatchedDate(r.assignedAt || r.createdAt); return d ? d.format('MM/DD HH:mm') : '-'; } },
    { title: 'Хүргэгдсэн цаг', dataIndex: 'deliveredAt', key: 'deliveredAt', width: 140,
      render: d => d ? dayjs(d).format('MM/DD HH:mm') : '-' },
    { title: 'Тайлбар', dataIndex: 'note', key: 'note', width: 200,
      render: n => n || '-' },

  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Хүргэлтүүд ({total})</h2>
        <Space wrap>
          <Input placeholder="Хайх..." prefix={<SearchOutlined />} value={search}
            onChange={e => setSearch(e.target.value)} onPressEnter={handleSearch}
            style={{ width: 200 }} allowClear />
          <Select allowClear placeholder="Төлөв" style={{ width: 150 }}
            onChange={v => { const f = { ...filters, status: v }; setFilters(f); setPage(1); fetchDeliveries(1, f); }}>
            {Object.entries(statusLabels).map(([k, v]) => <Select.Option key={k} value={k}>{v}</Select.Option>)}
          </Select>
          <RangePicker defaultValue={[dayjs(), dayjs()]}
            disabledDate={current => current && current < dayjs('2026-05-01').startOf('day')}
            onChange={dates => {
              const f = { ...filters };
              if (dates && dates[0] && dates[1]) {
                f.startDate = dates[0].format('YYYY-MM-DD');
                f.endDate = dates[1].format('YYYY-MM-DD');
              } else {
                delete f.startDate; delete f.endDate;
              }
              setFilters(f); setPage(1); fetchDeliveries(1, f);
            }} allowClear />
        </Space>
      </div>

      <Table columns={columns} dataSource={deliveries} rowKey="_id" loading={loading}
        pagination={{ current: page, total, pageSize: 50, onChange: p => { setPage(p); fetchDeliveries(p); } }}
        size="small"
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default Deliveries;